import React from 'react';
import { routeName as deriveRouteName } from '@/utilities/routeNames';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import CheckIcon from '@material-ui/icons/Check';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import ListIcon from '@material-ui/icons/List';
import { greens, golds, greys, onwardNavy } from '@/styles/colors';
import { orderMarginDollar, orderMarginPct, marginHealth, canViewShipperRates } from './utils';

const HEALTH_COLORS = { good: greens[0], thin: golds[0], bad: '#D23E3E', neutral: greys[1] };
const HEALTH_BG = { good: '#eaf6ec', thin: '#fbf1e0', bad: '#fbeaec', neutral: '#f0f0f0' };

const dollarFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const SidebarRoot = styled.div`
    width: 268px;
    min-width: 268px;
    background: #f7f7f9;
    border-right: 1px solid ${greys[4]};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
`;

const RouteHeader = styled.div`
    padding: 20px 16px 14px;
    border-bottom: 1px solid ${greys[4]};
`;

const RouteName = styled.div`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: ${onwardNavy};
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RouteSubtext = styled.div`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 500;
    font-size: 11px;
    color: ${greys[1]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const NavList = styled.div`
    flex: 1;
    padding: 8px 0;
`;

const NavItem = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: ${({ active }) => (active ? greys[5] : 'transparent')};
    border: none;
    cursor: pointer;
    text-align: left;
    border-left: 3px solid ${({ active }) => (active ? greens[0] : 'transparent')};
    transition: background 0.1s;
    &:hover {
        background: ${greys[5]};
    }
`;

const PipContainer = styled.div`
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 700;
    font-size: 12px;
`;

const NavItemText = styled.div`
    flex: 1;
    min-width: 0;
`;

const NavItemLabel = styled.div`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 600;
    font-size: 12px;
    color: ${greys[3]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const NavItemSub = styled.div`
    font-family: Montserrat, Roboto, Arial, sans-serif;
    font-weight: 500;
    font-size: 11px;
    color: ${greys[1]};
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
`;

function StatusPip({ index, audited, skipped, active }) {
    if (audited) {
        return (
            <PipContainer
                css={css`
                    background: ${greens[0]};
                `}
            >
                <CheckIcon
                    css={css`
                        font-size: 16px;
                        color: white;
                    `}
                />
            </PipContainer>
        );
    }
    if (skipped) {
        return (
            <PipContainer
                css={css`
                    background: white;
                    border: 2px dashed ${golds[0]};
                `}
            >
                <SkipNextIcon
                    css={css`
                        font-size: 14px;
                        color: ${golds[0]};
                    `}
                />
            </PipContainer>
        );
    }
    return (
        <PipContainer
            css={css`
                background: white;
                border: ${active ? `2px solid ${greens[0]}` : `1.5px solid ${greys[4]}`};
                color: ${active ? greens[0] : greys[2]};
            `}
        >
            {index + 1}
        </PipContainer>
    );
}

function MarginChip({ pct }) {
    const health = marginHealth(pct);
    return (
        <span
            css={css`
                font-family: Montserrat, Roboto, Arial, sans-serif;
                font-weight: 700;
                font-size: 10px;
                padding: 2px 7px;
                border-radius: 20px;
                color: ${HEALTH_COLORS[health]};
                background: ${HEALTH_BG[health]};
            `}
        >
            {pct != null ? `${pct.toFixed(1)}%` : '–'}
        </span>
    );
}

export default function Sidebar({
    mode = 'route',
    route,
    orders,
    screen,
    auditStates,
    onNavigate,
    canEditPerspective,
}) {
    const isQueue = mode === 'queue';
    const routeName = route ? deriveRouteName(route) || route.route_number || 'Route' : 'Non-Routed Orders';
    const firstOrder = orders[0] || {};
    const shipperName = firstOrder.order_shipper?.business_name || '–';
    const carrierName = firstOrder.order_carrier?.business_name || '–';
    const reviewedCount = orders.filter((o) => auditStates[o.order_id]?.audited).length;
    const progressLabel = `${reviewedCount} of ${orders.length} reviewed`;

    return (
        <SidebarRoot>
            <RouteHeader>
                {isQueue ? (
                    <>
                        <RouteName title="Audit Orders">Audit Orders</RouteName>
                        <RouteSubtext title={progressLabel}>{progressLabel}</RouteSubtext>
                    </>
                ) : (
                    <>
                        <RouteName title={routeName}>{routeName}</RouteName>
                        <RouteSubtext title={shipperName}>Shipper: {shipperName}</RouteSubtext>
                        <RouteSubtext title={carrierName}>Carrier: {carrierName}</RouteSubtext>
                    </>
                )}
            </RouteHeader>

            <NavList>
                {!isQueue && (
                    <NavItem active={screen === 'overview'} onClick={() => onNavigate('overview')}>
                        <PipContainer
                            css={css`
                                background: ${screen === 'overview' ? greens[0] : greys[4]};
                            `}
                        >
                            <ListIcon
                                css={css`
                                    font-size: 16px;
                                    color: white;
                                `}
                            />
                        </PipContainer>
                        <NavItemText>
                            <NavItemLabel>Route Overview</NavItemLabel>
                        </NavItemText>
                    </NavItem>
                )}

                {orders.map((order, idx) => {
                    const state = auditStates[order.order_id] || {};
                    const isActive = screen === idx;
                    const marginDollar = orderMarginDollar(order);
                    const marginPct = orderMarginPct(order);
                    // Margin exposes the shipper rate, so it follows shipper-perspective access.
                    const showMargin = canViewShipperRates(order, canEditPerspective);
                    const orderLabel =
                        order.order_number || order.po_number || order.reference_id || `Order ${idx + 1}`;

                    return (
                        <NavItem key={order.order_id} active={isActive} onClick={() => onNavigate(idx)}>
                            <StatusPip index={idx} audited={state.audited} skipped={state.skipped} active={isActive} />
                            <NavItemText>
                                <NavItemLabel title={orderLabel}>{orderLabel}</NavItemLabel>
                                {showMargin && (
                                    <NavItemSub>
                                        <span>{dollarFormatter.format(marginDollar)}</span>
                                        <MarginChip pct={marginPct} />
                                    </NavItemSub>
                                )}
                            </NavItemText>
                        </NavItem>
                    );
                })}
            </NavList>
        </SidebarRoot>
    );
}
